import { topshotsAPI } from '../../app/api';
// import { Event, EventCreation } from './events.types';
// import { Album } from '../albums/albums.types';
// import { Image } from '../images/images.types';
import {
  IAlbum,
  IEvent,
  IEventCreation,
  IImage,
  PagedRequest,
  PagedResponse,
} from '@topshots/core-models';

const eventsApi = topshotsAPI.injectEndpoints({
  endpoints: (build) => ({
    getEvents: build.query<
      PagedResponse<IEvent[]>,
      PagedRequest & { search?: string }
    >({
      query: (request) => ({
        url: 'events',
        method: 'GET',
        params: request,
      }),
      providesTags: (result, error, page) =>
        result
          ? [
              // Provides a tag for each enquiry in the current page,
              // as well as the 'PARTIAL-LIST' tag.
              ...result.data.map(({ _id }) => ({
                type: 'Events' as const,
                id: _id.toString(),
              })),
              { type: 'Events', id: 'PARTIAL-LIST' },
            ]
          : [{ type: 'Events', id: 'PARTIAL-LIST' }],
    }),
    getEventAlbums: build.query<
      PagedResponse<IAlbum[]>,
      PagedRequest & { eventId: string }
    >({
      query: (request) => ({
        url: `events/${request.eventId}/albums`,
        method: 'GET',
        params: request,
      }),
      providesTags: (result, error, page) =>
        result
          ? [
              // Provides a tag for each enquiry in the current page,
              // as well as the 'PARTIAL-LIST' tag.
              ...result.data.map(({ _id }) => ({
                type: 'Albums' as const,
                id: _id.toString(),
              })),
              { type: 'Albums', id: 'PARTIAL-LIST' },
            ]
          : [{ type: 'Albums', id: 'PARTIAL-LIST' }],
    }),
    getEvent: build.query<IEvent, string>({
      query: (id) => ({
        url: `events/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, page) => [
        { type: 'Events' as const, id: result?._id.toString() },
      ],
    }),
    setCover: build.mutation<IImage, { id: string; image: File }>({
      query: (request) => {
        const data = new FormData();
        data.append('file', request.image);
        return {
          url: `events/${request.id}/cover`,
          method: 'POST',
          body: data,
        };
      },
      invalidatesTags: (result, error, product) => [{ type: 'EventCover' }],
    }),
    getCover: build.query<IImage, string>({
      query: (id) => ({
        url: `events/${id}/cover`,
        method: 'GET',
      }),
      providesTags: (result, error, page) => [
        { type: 'EventCover' as const, id: result?.event_id.toString() },
      ],
    }),
    getEventTurnover: build.query<
      { total: number; orderCount: number },
      string
    >({
      query: (id) => ({
        url: `events/${id}/stats/turnover`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [
        { type: 'EventTurnover' as const, id },
      ],
    }),
    deleteCover: build.mutation<void, string>({
      query: (id) => ({
        url: `events/${id}/cover`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, product) => [{ type: 'EventCover' }],
    }),
    createEvent: build.mutation<IEvent, IEventCreation>({
      query: (request) => ({
        url: `events`,
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (result, error, product) => [
        { type: 'Events', id: 'PARTIAL-LIST' },
        { type: 'Events', id: result?._id.toString() },
      ],
    }),
    updateEvent: build.mutation<IEvent, { id: string; data: Partial<IEvent> }>({
      query: (request) => ({
        url: `events/${request.id}`,
        method: 'PUT',
        body: request.data,
      }),
      invalidatesTags: (result, error, product) => [
        { type: 'Events', id: 'PARTIAL-LIST' },
        { type: 'Events', id: result?._id.toString() },
      ],
    }),
    deleteEvent: build.mutation<IEvent, string>({
      query: (id) => ({
        url: `events/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, product) => [
        { type: 'Events', id: 'PARTIAL-LIST' },
        { type: 'Events', id: result?._id.toString() },
      ],
    }),
  }),
});

export const {
  useGetEventQuery,
  useGetEventsQuery,
  useUpdateEventMutation,
  useGetEventAlbumsQuery,
  useGetCoverQuery,
  useSetCoverMutation,
  useDeleteCoverMutation,
  useDeleteEventMutation,
  useCreateEventMutation,
  useGetEventTurnoverQuery,
} = eventsApi;
